/**
 * ReviewPanel Component
 *
 * A slide-out panel for reviewing transaction classifications with LLM suggestions.
 * Displays transaction details, AI suggestions with confidence badges,
 * proposed rules, and action buttons.
 *
 * @see types/accounting.ts for Transaction and Account types
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { X, Sparkles, Layers, Loader2, ChevronDown, Plus, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import type { Transaction, Account, CreateRuleRequest, RuleTestMatch } from '@/types/accounting';
import type { Tag, Tagging } from '@/types/accountingTags';
import { AccountTreeSelect } from '../../shared/AccountTreeSelect';
import { RuleFormModal, type SourceTransaction } from '../../ClassificationRules/components/RuleFormModal';
import { TransactionTagEditor } from '@/components/accounting/tags/TransactionTagEditor';
import * as accountingTagsApi from '@/services/accountingTagsApi';
import { getTagString } from '@/types/accountingTags';
import { TaggingRuleFormModal } from './TaggingRuleFormModal';

export interface ReviewPanelProps {
  /** Transaction being reviewed */
  transaction: Transaction;
  /** Flat list of all accounts for lookups */
  accounts: Account[];
  /** Hierarchical tree structure for category selection */
  accountTree: Account[];
  /** Callback when panel should close */
  onClose: () => void;
  /** Callback when transaction is accepted (optional, called with updated transaction) */
  onAccept?: (transaction: Transaction) => void;
  /** Callback to test a pattern against transactions (for RuleFormModal) */
  onTestPattern?: (pattern: string, direction?: string) => Promise<{ matchingTransactions: RuleTestMatch[]; matchCount: number }>;
  /** Callback when a rule is created (for RuleFormModal) */
  onRuleCreate?: (data: CreateRuleRequest) => Promise<number | void>;
}

/**
 * Format date for display (DD.MM.YYYY)
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Get confidence color scheme based on confidence value
 */
function getConfidenceColors(confidence: number | null | undefined): {
  border: string;
  background: string;
  iconBg: string;
  labelColor: string;
  confidenceColor: string;
} {
  const isHighConfidence = (confidence ?? 0) >= 0.7;

  if (isHighConfidence) {
    return {
      border: 'border-blue-500',
      background: 'bg-gradient-to-br from-blue-50 to-blue-100',
      iconBg: 'bg-blue-500',
      labelColor: 'text-blue-700',
      confidenceColor: 'text-blue-700',
    };
  }

  return {
    border: 'border-amber-400',
    background: 'bg-gradient-to-br from-amber-50 to-amber-100',
    iconBg: 'bg-amber-500',
    labelColor: 'text-amber-700',
    confidenceColor: 'text-amber-700',
  };
}

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken(): string {
  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
    ''
  );
}

/**
 * ReviewPanel renders a slide-out panel for reviewing transaction classifications.
 *
 * Features:
 * - Transaction detail header (description, date, amount, owner)
 * - LLM suggestion box with confidence badge
 * - LLM reasoning text display
 * - Proposed rule box (when suggestedPattern exists)
 * - Accept and Accept+Rule buttons with loading states
 * - Alternative category selection (collapsible)
 * - Keyboard shortcuts (Enter, Shift+Enter, Esc, Tab)
 * - Close button
 *
 * Keyboard Shortcuts:
 * - Enter: Accept suggestion
 * - Shift+Enter: Accept + Create Rule
 * - Esc: Close panel
 * - Tab: Focus alternatives section (expand if collapsed)
 *
 * @example
 * ```tsx
 * <ReviewPanel
 *   transaction={transaction}
 *   accounts={accounts}
 *   accountTree={accountTree}
 *   onClose={() => setReviewingTransaction(null)}
 *   onAccept={(updated) => reloadTransactions()}
 * />
 * ```
 */
export function ReviewPanel({
  transaction,
  accounts,
  accountTree,
  onClose,
  onAccept,
  onTestPattern,
  onRuleCreate,
}: ReviewPanelProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'accept' | 'acceptWithRule' | 'manual' | null>(null);
  const [alternativesExpanded, setAlternativesExpanded] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Tags state
  const [taggings, setTaggings] = useState<Tagging[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [showTaggingRuleModal, setShowTaggingRuleModal] = useState(false);

  // RuleFormModal state
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleModalContext, setRuleModalContext] = useState<{
    targetAccountId: number;
    classifyAfterCreate: boolean;
  } | null>(null);

  // Get suggested account from classificationSuggestions
  const suggestedAccount = useMemo(() => {
    if (!transaction.classificationSuggestions) return null;
    return accounts.find(
      (a) => a.id === transaction.classificationSuggestions?.accountId
    ) ?? null;
  }, [transaction.classificationSuggestions, accounts]);

  const confidence = transaction.classificationConfidence;
  const reasoning = transaction.classificationReasoning;
  const suggestedPattern = transaction.classificationSuggestions?.suggestedPattern;
  const hasSuggestion = suggestedAccount !== null;

  const colors = getConfidenceColors(confidence);

  /**
   * Handle accepting the LLM suggestion
   */
  const handleAccept = useCallback(
    async (createRule: boolean) => {
      if (isLoading || !hasSuggestion) return;

      setIsLoading(true);
      setLoadingAction(createRule ? 'acceptWithRule' : 'accept');

      try {
        const response = await fetch(
          `/api/accounting/transactions/${transaction.id}/classify`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': getCsrfToken(),
            },
            body: JSON.stringify({
              fromSuggestion: true,
              createRule,
            }),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to classify transaction');
        }

        const result = await response.json();

        if (createRule && result.rule) {
          toast.success('Transaction classified and rule created');
        } else {
          toast.success('Transaction classified');
        }

        // Call onAccept callback with updated transaction
        onAccept?.(result.transaction);

        // Close the panel
        onClose();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to classify transaction'
        );
      } finally {
        setIsLoading(false);
        setLoadingAction(null);
      }
    },
    [transaction.id, isLoading, hasSuggestion, onAccept, onClose]
  );

  /**
   * Handle manual classification with selected account (classify only, no rule)
   */
  const handleManualClassify = useCallback(async () => {
    if (isLoading || !selectedAccountId) return;

    setIsLoading(true);
    setLoadingAction('manual');

    try {
      const response = await fetch(
        `/api/accounting/transactions/${transaction.id}/classify`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify({
            accountId: selectedAccountId,
            owner: transaction.owner,
            createRule: false,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to classify transaction');
      }

      const result = await response.json();
      toast.success('Transaction classified');

      // Call onAccept callback with updated transaction
      onAccept?.(result.transaction);

      // Close the panel
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to classify transaction'
      );
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [transaction.id, transaction.owner, isLoading, selectedAccountId, onAccept, onClose]);

  /**
   * Build SourceTransaction object from current transaction
   */
  const sourceTransaction: SourceTransaction = useMemo(() => ({
    id: transaction.id,
    description: transaction.description,
    direction: transaction.direction,
    owner: transaction.owner,
  }), [transaction.id, transaction.description, transaction.direction, transaction.owner]);

  /**
   * Open RuleFormModal for creating rule from current transaction
   */
  const handleOpenRuleModal = useCallback((targetAccountId: number, classifyAfterCreate: boolean) => {
    setRuleModalContext({ targetAccountId, classifyAfterCreate });
    setShowRuleModal(true);
  }, []);

  /**
   * Handle closing the rule modal
   */
  const handleCloseRuleModal = useCallback(() => {
    setShowRuleModal(false);
    setRuleModalContext(null);
  }, []);

  /**
   * Handle rule creation from modal
   */
  const handleRuleCreate = useCallback(async (data: CreateRuleRequest): Promise<number | void> => {
    if (!onRuleCreate) {
      // If no external onCreate provided, make API call directly
      const response = await fetch('/api/accounting/classification_rules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create rule');
      }

      const result = await response.json();
      toast.success('Rule created successfully');
      return result.rule.id;
    }

    const ruleId = await onRuleCreate(data);
    toast.success('Rule created successfully');
    return ruleId;
  }, [onRuleCreate]);

  /**
   * Handle rule creation with classify (from modal "Create & Classify" button)
   */
  const handleCreateAndClassify = useCallback(async (_ruleId: number): Promise<void> => {
    if (!ruleModalContext) return;

    // Classify the transaction with the selected account
    const response = await fetch(
      `/api/accounting/transactions/${transaction.id}/classify`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          accountId: ruleModalContext.targetAccountId,
          owner: transaction.owner,
          createRule: false, // Rule already created
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to classify transaction');
    }

    const result = await response.json();
    toast.success('Transaction classified and rule created');

    // Call onAccept callback with updated transaction
    onAccept?.(result.transaction);

    // Close both modal and panel
    handleCloseRuleModal();
    onClose();
  }, [transaction.id, transaction.owner, ruleModalContext, onAccept, onClose, handleCloseRuleModal]);

  /**
   * Dummy onUpdate handler for RuleFormModal (not used in create mode)
   */
  const handleRuleUpdate = useCallback(async () => {
    // This is a no-op since we only use the modal for creating rules
  }, []);

  /**
   * Default test pattern handler if none provided
   */
  const handleTestPattern = useCallback(async (pattern: string, direction?: string) => {
    if (onTestPattern) {
      return onTestPattern(pattern, direction);
    }

    // Default implementation using API
    const response = await fetch('/api/accounting/classification_rules/test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify({ pattern, direction }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to test pattern');
    }

    return response.json();
  }, [onTestPattern]);

  /**
   * Handle adding a tag to the transaction
   */
  const handleAddTag = useCallback(async (tag: Tag) => {
    setTagsLoading(true);
    try {
      const tagString = getTagString(tag);
      const newTagging = await accountingTagsApi.addTagToTransaction(transaction.id, tagString);
      setTaggings((prev) => [...prev, newTagging]);
      toast.success('Tag added');
    } catch (error) {
      console.error('Failed to add tag:', error);
      toast.error('Failed to add tag');
    } finally {
      setTagsLoading(false);
    }
  }, [transaction.id]);

  /**
   * Handle removing a tag from the transaction
   */
  const handleRemoveTag = useCallback(async (tagging: Tagging) => {
    setTagsLoading(true);
    try {
      const tagString = getTagString(tagging.tag);
      await accountingTagsApi.removeTagFromTransaction(transaction.id, tagString);
      setTaggings((prev) => prev.filter((t) => t.id !== tagging.id));
      toast.success('Tag removed');
    } catch (error) {
      console.error('Failed to remove tag:', error);
      toast.error('Failed to remove tag');
    } finally {
      setTagsLoading(false);
    }
  }, [transaction.id]);

  /**
   * Handle tagging rule creation success
   */
  const handleTaggingRuleSuccess = useCallback(async (_ruleId: number) => {
    // Refresh tags after rule creation
    try {
      const transactionTags = await accountingTagsApi.getTransactionTags(transaction.id);
      const taggingsData: Tagging[] = transactionTags.map((tt) => ({
        id: tt.taggingId,
        tagId: tt.id,
        transactionId: transaction.id,
        source: tt.source,
        confidence: tt.confidence,
        tag: {
          id: tt.id,
          tagType: tt.tagType,
          value: tt.value,
          displayName: tt.displayName,
          color: tt.color,
        },
      }));
      setTaggings(taggingsData);
    } catch (error) {
      console.error('Failed to refresh tags:', error);
    }
    setShowTaggingRuleModal(false);
  }, [transaction.id]);

  /**
   * Handle keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts if focus is in an input or select
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
                             activeElement?.tagName === 'TEXTAREA' ||
                             activeElement?.getAttribute('role') === 'combobox';

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      // Only handle these shortcuts if not in an input
      if (!isInputFocused) {
        if (e.key === 'Enter' && !e.shiftKey && hasSuggestion && !isLoading) {
          e.preventDefault();
          handleAccept(false);
        } else if (e.key === 'Enter' && e.shiftKey && hasSuggestion && suggestedPattern && !isLoading) {
          e.preventDefault();
          handleAccept(true);
        } else if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          setAlternativesExpanded(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, hasSuggestion, suggestedPattern, isLoading, handleAccept]);

  // Fetch tags when panel opens
  useEffect(() => {
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        // Fetch transaction tags and available tags in parallel
        const [transactionTags, allTags] = await Promise.all([
          accountingTagsApi.getTransactionTags(transaction.id),
          accountingTagsApi.getTags(),
        ]);

        // Convert TransactionTag[] to Tagging[]
        const taggingsData: Tagging[] = transactionTags.map((tt) => ({
          id: tt.taggingId,
          tagId: tt.id,
          transactionId: transaction.id,
          source: tt.source,
          confidence: tt.confidence,
          tag: {
            id: tt.id,
            tagType: tt.tagType,
            value: tt.value,
            displayName: tt.displayName,
            color: tt.color,
          },
        }));

        setTaggings(taggingsData);
        setAvailableTags(allTags);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        toast.error('Failed to load tags');
      } finally {
        setTagsLoading(false);
      }
    };

    fetchTags();
  }, [transaction.id]);

  // Focus the panel when it mounts
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50"
      data-testid="review-panel"
    >
      {/* Backdrop overlay */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        data-testid="review-panel-backdrop"
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="absolute top-0 right-0 w-[480px] h-full bg-white shadow-xl overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-panel-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2
            id="review-panel-title"
            className="text-lg font-semibold text-gray-900"
          >
            Review Transaction
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close panel"
            data-testid="review-panel-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Transaction Details */}
          <div
            className="bg-gray-50 rounded-lg p-4"
            data-testid="transaction-details"
          >
            <p className="text-lg font-semibold text-gray-900 mb-2">
              {transaction.description}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span data-testid="transaction-date">
                {formatDate(transaction.date)}
              </span>
              <span data-testid="transaction-amount">
                {transaction.amountDisplay}
              </span>
              <span
                className="capitalize"
                data-testid="transaction-owner"
              >
                {transaction.owner}
              </span>
            </div>

            {/* Classification Rule Info - shown when a rule was applied */}
            {transaction.classificationRule && (
              <div
                className="mt-3 pt-3 border-t border-gray-200"
                data-testid="applied-rule-info"
              >
                <div className="flex items-center gap-2 text-sm">
                  <FileCheck className="w-4 h-4 text-green-600" />
                  <span className="text-gray-600">Classified by rule:</span>
                  <span className="font-medium text-gray-900">
                    {transaction.classificationRule.name}
                  </span>
                </div>
                <code
                  className="mt-1.5 block text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 font-mono"
                  data-testid="applied-rule-pattern"
                >
                  {transaction.classificationRule.pattern}
                </code>
              </div>
            )}
          </div>

          {/* LLM Suggestion Box */}
          {hasSuggestion && (
            <div
              className={clsx(
                'rounded-xl p-5 border-2',
                colors.border,
                colors.background
              )}
              data-testid="llm-suggestion-box"
            >
              {/* Suggestion Header */}
              <div className="flex items-center gap-2 mb-3">
                <div
                  className={clsx(
                    'w-6 h-6 rounded-full flex items-center justify-center text-white',
                    colors.iconBg
                  )}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <span
                  className={clsx(
                    'text-xs font-semibold uppercase tracking-wider',
                    colors.labelColor
                  )}
                >
                  AI Suggestion
                </span>
                {confidence != null && (
                  <span
                    className={clsx(
                      'ml-auto text-sm',
                      colors.confidenceColor
                    )}
                    data-testid="confidence-badge"
                  >
                    {Math.round(confidence * 100)}% confident
                  </span>
                )}
              </div>

              {/* Suggested Category */}
              <p
                className="text-xl font-semibold text-gray-900 mb-2"
                data-testid="suggested-category"
              >
                {suggestedAccount.name}
              </p>

              {/* Reasoning */}
              {reasoning && (
                <p
                  className="text-sm text-gray-600 italic mb-4"
                  data-testid="llm-reasoning"
                >
                  "{reasoning}"
                </p>
              )}

              {/* Proposed Rule Box */}
              {suggestedPattern && (
                <div
                  className="bg-white border border-blue-200 rounded-lg p-3 mb-4"
                  data-testid="proposed-rule-box"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">
                      Proposed Rule
                    </span>
                  </div>
                  <code
                    className="block bg-gray-100 px-3 py-2 rounded text-sm font-mono text-gray-700"
                    data-testid="rule-pattern"
                  >
                    {suggestedPattern}
                  </code>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3" data-testid="action-buttons">
                <button
                  type="button"
                  onClick={() => handleAccept(false)}
                  disabled={isLoading}
                  className={clsx(
                    'flex-1 py-3 px-4 rounded-lg font-semibold text-white',
                    'bg-green-500 hover:bg-green-600',
                    'disabled:bg-green-300 disabled:cursor-not-allowed',
                    'transition-colors',
                    'flex items-center justify-center gap-2'
                  )}
                  data-testid="accept-button"
                >
                  {loadingAction === 'accept' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  Accept
                </button>

                {suggestedPattern && (
                  <button
                    type="button"
                    onClick={() => handleAccept(true)}
                    disabled={isLoading}
                    className={clsx(
                      'flex-1 py-3 px-4 rounded-lg font-semibold text-white',
                      'bg-blue-500 hover:bg-blue-600',
                      'disabled:bg-blue-300 disabled:cursor-not-allowed',
                      'transition-colors',
                      'flex items-center justify-center gap-2'
                    )}
                    data-testid="accept-with-rule-button"
                  >
                    {loadingAction === 'acceptWithRule' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : null}
                    Accept + Create Rule
                  </button>
                )}
              </div>

              {/* Create Rule from This Transaction - opens modal */}
              <button
                type="button"
                onClick={() => handleOpenRuleModal(suggestedAccount.id, false)}
                disabled={isLoading}
                className={clsx(
                  'w-full mt-3 py-2.5 px-4 rounded-lg font-medium',
                  'bg-gray-100 hover:bg-gray-200 text-gray-700',
                  'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                  'transition-colors',
                  'flex items-center justify-center gap-2'
                )}
                data-testid="create-rule-from-transaction-button"
              >
                <Plus className="w-4 h-4" />
                Create Rule from This Transaction
              </button>
            </div>
          )}

          {/* Alternative Category Selection - Collapsible when suggestion exists */}
          {hasSuggestion && (
            <div className="border-t pt-4" data-testid="alternatives-section">
              <button
                type="button"
                onClick={() => setAlternativesExpanded(!alternativesExpanded)}
                className="flex items-center justify-between w-full py-2 text-sm text-gray-600 hover:text-gray-900"
                aria-expanded={alternativesExpanded}
                data-testid="alternatives-toggle"
              >
                <span className="font-medium">Choose different category</span>
                <ChevronDown
                  className={clsx(
                    'w-4 h-4 transition-transform duration-200',
                    alternativesExpanded && 'rotate-180'
                  )}
                />
              </button>

              {alternativesExpanded && (
                <div className="mt-3 space-y-3" data-testid="alternatives-content">
                  <AccountTreeSelect
                    accounts={accounts}
                    accountTree={accountTree}
                    value={selectedAccountId}
                    onChange={setSelectedAccountId}
                    filterTypes={['expense', 'income']}
                    placeholder="Select a category..."
                    disabled={isLoading}
                  />

                  {/* Action buttons - only shown when category is selected */}
                  {selectedAccountId && (
                    <div className="flex flex-col gap-2" data-testid="alternative-action-buttons">
                      <button
                        type="button"
                        onClick={handleManualClassify}
                        disabled={isLoading}
                        className={clsx(
                          'w-full py-2.5 px-4 rounded-lg font-medium text-white',
                          'bg-green-500 hover:bg-green-600',
                          'disabled:bg-green-300 disabled:cursor-not-allowed',
                          'transition-colors',
                          'flex items-center justify-center gap-2'
                        )}
                        data-testid="classify-as-selected-button"
                      >
                        {loadingAction === 'manual' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : null}
                        Classify as Selected
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenRuleModal(selectedAccountId, true)}
                        disabled={isLoading}
                        className={clsx(
                          'w-full py-2.5 px-4 rounded-lg font-medium text-white',
                          'bg-blue-500 hover:bg-blue-600',
                          'disabled:bg-blue-300 disabled:cursor-not-allowed',
                          'transition-colors',
                          'flex items-center justify-center gap-2'
                        )}
                        data-testid="classify-and-create-rule-button"
                      >
                        <Plus className="w-4 h-4" />
                        Classify + Create Rule...
                      </button>
                      <p className="text-xs text-gray-500 text-center">
                        Opens modal with regex pattern builder
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Fallback when no suggestion - show category selection directly */}
          {!hasSuggestion && (
            <div data-testid="no-suggestion-fallback">
              <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center mb-4">
                <Sparkles className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  No AI suggestion available for this transaction.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Select a category manually below.
                </p>
              </div>

              <div className="space-y-3" data-testid="manual-classification-section">
                <label className="block text-sm font-medium text-gray-700">
                  Select Category
                </label>
                <AccountTreeSelect
                  accounts={accounts}
                  accountTree={accountTree}
                  value={selectedAccountId}
                  onChange={setSelectedAccountId}
                  filterTypes={['expense', 'income']}
                  placeholder="Select a category..."
                  disabled={isLoading}
                />

                {/* Action buttons - only shown when category is selected */}
                {selectedAccountId && (
                  <div className="flex flex-col gap-2" data-testid="manual-action-buttons">
                    <button
                      type="button"
                      onClick={handleManualClassify}
                      disabled={isLoading}
                      className={clsx(
                        'w-full py-3 px-4 rounded-lg font-semibold text-white',
                        'bg-green-500 hover:bg-green-600',
                        'disabled:bg-green-300 disabled:cursor-not-allowed',
                        'transition-colors',
                        'flex items-center justify-center gap-2'
                      )}
                      data-testid="classify-transaction-button"
                    >
                      {loadingAction === 'manual' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : null}
                      Classify Transaction
                    </button>

                    <button
                      type="button"
                      onClick={() => handleOpenRuleModal(selectedAccountId, true)}
                      disabled={isLoading}
                      className={clsx(
                        'w-full py-2.5 px-4 rounded-lg font-medium text-white',
                        'bg-blue-500 hover:bg-blue-600',
                        'disabled:bg-blue-300 disabled:cursor-not-allowed',
                        'transition-colors',
                        'flex items-center justify-center gap-2'
                      )}
                      data-testid="classify-and-create-rule-button"
                    >
                      <Plus className="w-4 h-4" />
                      Classify + Create Rule...
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      Opens modal with regex pattern builder
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags Section - Collapsible */}
          <div className="border-t pt-4" data-testid="tags-section">
            <button
              type="button"
              onClick={() => setTagsExpanded(!tagsExpanded)}
              className="flex items-center justify-between w-full py-2 text-sm text-gray-600 hover:text-gray-900"
              aria-expanded={tagsExpanded}
              data-testid="tags-toggle"
            >
              <span className="font-medium">
                Tags
                {!tagsExpanded && taggings.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {taggings.length}
                  </span>
                )}
              </span>
              <ChevronDown
                className={clsx(
                  'w-4 h-4 transition-transform duration-200',
                  tagsExpanded && 'rotate-180'
                )}
              />
            </button>

            {tagsExpanded && (
              <div className="mt-3 space-y-3" data-testid="tags-content">
                <TransactionTagEditor
                  taggings={taggings}
                  availableTags={availableTags}
                  onAddTag={handleAddTag}
                  onRemoveTag={handleRemoveTag}
                  disabled={tagsLoading}
                />

                {/* Create Tagging Rule from This Transaction button */}
                <button
                  type="button"
                  onClick={() => setShowTaggingRuleModal(true)}
                  disabled={tagsLoading}
                  className={clsx(
                    'w-full py-2.5 px-4 rounded-lg font-medium',
                    'bg-gray-100 hover:bg-gray-200 text-gray-700',
                    'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                    'transition-colors',
                    'flex items-center justify-center gap-2'
                  )}
                  data-testid="create-tagging-rule-button"
                >
                  <Plus className="w-4 h-4" />
                  Create Tagging Rule from This Transaction...
                </button>
              </div>
            )}
          </div>

          {/* Keyboard Shortcuts Hint */}
          {hasSuggestion && (
            <div
              className="text-xs text-gray-500 space-y-1 p-3 bg-gray-50 rounded-lg"
              data-testid="keyboard-hints"
            >
              <div><kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-700">Enter</kbd> Accept suggestion</div>
              {suggestedPattern && (
                <div><kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-700">Shift</kbd>+<kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-700">Enter</kbd> Accept + Create rule</div>
              )}
              <div><kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-700">Tab</kbd> Show alternatives</div>
              <div><kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-700">Esc</kbd> Close panel</div>
            </div>
          )}
        </div>
      </div>

      {/* RuleFormModal for creating rules from transactions */}
      <RuleFormModal
        isOpen={showRuleModal}
        onClose={handleCloseRuleModal}
        onCreate={handleRuleCreate}
        onUpdate={handleRuleUpdate}
        onTestPattern={handleTestPattern}
        rule={null}
        accounts={accounts}
        accountTree={accountTree}
        sourceTransaction={sourceTransaction}
        initialTargetAccountId={ruleModalContext?.targetAccountId}
        onCreateAndClassify={ruleModalContext?.classifyAfterCreate ? handleCreateAndClassify : undefined}
      />

      {/* TaggingRuleFormModal for creating tagging rules from transactions */}
      <TaggingRuleFormModal
        isOpen={showTaggingRuleModal}
        onClose={() => setShowTaggingRuleModal(false)}
        sourceTransaction={{
          id: transaction.id,
          description: transaction.description,
        }}
        availableTags={availableTags}
        onSuccess={handleTaggingRuleSuccess}
      />
    </div>
  );
}

export default ReviewPanel;
